import React, { useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './CancelSessionModal.css';
import CancelIcon from '../../../assets/Model/reject-icon.svg';
import CancelSuccessDialog from './CancelSuccessDialog';
import { toast } from 'react-hot-toast';
import { axiosInstance } from '../../../utils/tokenUtils';
import InfoIcon from '@mui/icons-material/Info';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '8px',
  width: '500px',
  padding: '24px',
};

// Predefined reasons for cancellation
const predefinedReasons = [
  'Not available at this time',
  'Personal emergency',
  'Health issues',
  'Technical difficulties',
];

const CancelSessionModal = ({ open, onClose, sessionId, sessionType = 'one_on_one' }) => {
  const [reason, setReason] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedReasonIndex, setSelectedReasonIndex] = useState(-1);

  // Allow cancellation for both one-on-one and group sessions
  const canCancel = sessionType === 'one_on_one' || sessionType === 'group';

  const handleReasonCardClick = (reason, index) => {
    setReason(reason);
    setSelectedReasonIndex(index);
    setError(''); // Clear any existing error
  };

  const handleConfirm = async () => {
    if (!canCancel) return;

    if (!reason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Make API call to cancel session
      ///teachers/sessions/cancel/:session_id
      const response = await axiosInstance.patch(`/teachers/sessions/cancel/${sessionId}`, {
        cancellation_reason: reason.trim(),
      });
      console.log(response);
      if (response?.status === 200) {
        onClose(); // Close the cancel modal
        setShowSuccess(true); // Show success dialog
        toast.success('Session cancelled successfully');
      }
    } catch (error) {
      console.error('Error cancelling session:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to cancel session. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setReason('');
    setSelectedReasonIndex(-1);
  };

  const handleClose = () => {
    setReason('');
    setError('');
    setSelectedReasonIndex(-1);
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Box className="flex justify-end">
            <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer', color: '#666666' }} />
          </Box>
          <Box className="flex flex-col items-center gap-4">
            <img
              src={CancelIcon}
              alt="Cancel"
              className="cancel-icon"
              style={{ width: 200, height: 100 }}
            />
            <Typography className="cancel-title">Cancel Session</Typography>

            {canCancel ? (
              <>
                <Typography className="cancel-message">
                  No charges will apply as you're cancelling more than 8 hours in advance.
                </Typography>

                <Box className="w-full">
                  <Typography className="reason-label">
                    Reason<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <textarea
                    className={`reason-input ${error ? 'error-border' : ''}`}
                    placeholder="Type here..."
                    value={reason}
                    onChange={e => {
                      setReason(e.target.value);
                      setSelectedReasonIndex(-1); // Clear selected card when typing
                      if (e.target.value.trim()) {
                        setError('');
                      }
                    }}
                    rows={2}
                  />
                  {error && <Typography className="error-text">{error}</Typography>}

                  {/* Predefined reason cards */}
                  <Box className="reason-cards-container">
                    {predefinedReasons.map((predefinedReason, index) => (
                      <Box
                        key={index}
                        className={`reason-card ${selectedReasonIndex === index ? 'selected' : ''}`}
                        onClick={() => handleReasonCardClick(predefinedReason, index)}
                      >
                        {predefinedReason}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box className="flex gap-6 w-full">
                  <div className="flex-1">
                    <button
                      className="cancel-button-session"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      No
                    </button>
                  </div>
                  <div className="flex-1">
                    <button
                      className="confirm-button"
                      onClick={handleConfirm}
                      disabled={loading || !reason.trim()}
                    >
                      {loading ? 'Processing...' : 'Confirm'}
                    </button>
                  </div>
                </Box>
              </>
            ) : (
              <Box
                className="flex flex-col items-center gap-4"
                sx={{
                  padding: '20px',
                  backgroundColor: '#FFF8E1',
                  borderRadius: '8px',
                  width: '100%',
                }}
              >
                <Box className="flex items-center gap-2">
                  <InfoIcon sx={{ color: '#FF9800' }} />
                  <Typography sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                    Prime Sessions Cannot Be Cancelled
                  </Typography>
                </Box>
                <Typography sx={{ textAlign: 'center' }}>
                  Instructors can cancel One-on-One and Group sessions by themselves. For Prime
                  session cancellations, please contact the administrator.
                </Typography>

                <button
                  className="cancel-button"
                  onClick={handleClose}
                  style={{ marginTop: '10px', width: '100%' }}
                >
                  Close
                </button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <CancelSuccessDialog open={showSuccess} onClose={handleSuccessClose} />
    </>
  );
};

export default CancelSessionModal;
